import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SectionHeading from "../Shared/SectionHeading";
import { galleryImages } from "./galleryData";

const previewImages = galleryImages.slice(0, 4);

export const GallerySection = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const prefersReducedMotion = useReducedMotion();

  const handleOpen = (index: number) => {
    setSelectedImageIndex(index);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);
  const handleNext = () =>
    setSelectedImageIndex((prev) => (prev + 1) % previewImages.length);
  const handlePrev = () =>
    setSelectedImageIndex(
      (prev) => (prev - 1 + previewImages.length) % previewImages.length,
    );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;
      if (event.key === "ArrowRight") {
        setSelectedImageIndex((prev) => (prev + 1) % previewImages.length);
      }
      if (event.key === "ArrowLeft") {
        setSelectedImageIndex(
          (prev) => (prev - 1 + previewImages.length) % previewImages.length,
        );
      }
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  return (
    <Box
      id="gallery-section"
      sx={{ px: { xs: 1.2, sm: 1.8, md: 2.6 }, py: { xs: 4.6, md: 5.8 } }}
    >
      <SectionHeading
        eyebrow="Selected Work"
        title="A Glimpse Into Recent Projects"
        subtitle="Explore a curated set of spaces where lighting, materials, and layout come together with a refined design language."
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "none",
          mx: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: { xs: 1.15, md: 1.5 },
        }}
      >
        {previewImages.map((image, index) => (
          <motion.div
            key={image}
            initial={prefersReducedMotion ? false : { opacity: 0, y: 22, scale: 0.985 }}
            whileInView={
              prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }
            }
            viewport={{ once: true, amount: 0.25 }}
            transition={
              prefersReducedMotion
                ? undefined
                : {
                    duration: 0.58,
                    delay: index * 0.08,
                    ease: [0.22, 1, 0.36, 1],
                  }
            }
          >
            <Box
              sx={{
                position: "relative",
                borderRadius: { xs: "16px", md: "20px" },
                overflow: "hidden",
                minHeight: { xs: 210, sm: 255, md: 305 },
                cursor: "pointer",
                boxShadow: "0 14px 30px rgba(20,8,10,0.12)",
                "&:before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(12,8,10,0.05) 42%, rgba(12,8,10,0.58) 100%)",
                  zIndex: 1,
                },
                "&:hover img": {
                  transform: "scale(1.05)",
                },
                "&:hover .gallery-overlay": {
                  opacity: 1,
                },
              }}
              onClick={() => handleOpen(index)}
            >
              <Box
                component="img"
                src={image}
                alt={`Featured interior project ${index + 1}`}
                loading={index < 2 ? "eager" : "lazy"}
                decoding="async"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "transform 360ms ease",
                }}
                onContextMenu={(event) => event.preventDefault()}
              />

              <Box
                className="gallery-overlay"
                sx={{
                  position: "absolute",
                  zIndex: 2,
                  inset: 0,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "flex-end",
                  p: { xs: 1.2, md: 1.6 },
                  opacity: { xs: 1, md: 0.7 },
                  transition: "opacity 240ms ease",
                }}
              >
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.75rem",
                    letterSpacing: "0.09em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  Open
                </Typography>
              </Box>
            </Box>
          </motion.div>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 4.3 }}>
        <Button
          onClick={() => navigate("/gallery")}
          sx={{
            px: 2.7,
            py: 1.08,
            borderRadius: "999px",
            textTransform: "none",
            fontFamily: '"Manrope", sans-serif',
            fontWeight: 700,
            color: "#fff",
            background: "linear-gradient(137deg, #803341 0%, #a85156 100%)",
            boxShadow: "0 10px 18px rgba(128,51,65,0.28)",
            "&:hover": {
              background: "linear-gradient(137deg, #6f2c39 0%, #954349 100%)",
            },
          }}
        >
          View Full Gallery
        </Button>
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(11,8,9,0.92)",
            backdropFilter: "blur(5px)",
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: isMobile ? 24 : 30,
              right: isMobile ? 20 : 30,
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.14)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.24)",
              },
            }}
            onClick={handleClose}
          >
            <CloseRoundedIcon />
          </IconButton>

          <IconButton
            sx={{
              position: "absolute",
              left: isMobile ? 14 : 26,
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.14)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.24)",
              },
            }}
            onClick={handlePrev}
          >
            <ArrowBackIosNewRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Box
            component="img"
            src={previewImages[selectedImageIndex]}
            alt={`Gallery project ${selectedImageIndex + 1}`}
            sx={{
              width: "min(92vw, 1080px)",
              height: "min(82vh, 760px)",
              objectFit: "cover",
              borderRadius: "16px",
              boxShadow: "0 28px 60px rgba(0,0,0,0.42)",
            }}
            onContextMenu={(event) => event.preventDefault()}
          />

          <IconButton
            sx={{
              position: "absolute",
              right: isMobile ? 14 : 26,
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.14)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.24)",
              },
            }}
            onClick={handleNext}
          >
            <ArrowForwardIosRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Typography
            sx={{
              position: "absolute",
              bottom: isMobile ? 18 : 24,
              color: "rgba(255,255,255,0.88)",
              fontFamily: '"Manrope", sans-serif',
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            {selectedImageIndex + 1} / {previewImages.length}
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};
